import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { hasPendingTimelineEdit } from "./edit-ownership";
import { incomingTrackIndex, type InsertionTarget } from "./incoming-media";
import { trackKind, type TrackKind } from "./track-layout";
import { openPlacementSpace } from "./placement-space";
import { captureInsertionTarget } from "./incoming-media";
import type { TrackType } from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { AssetDropTrackTarget } from "../../vendor/src/features/remotion-editor/editor/utils/track-position-utils";

export function isIncomingPreviewCurrent(
  state: EditorState,
  preview: {
    baseDocument?: EditorState["undoableState"];
    baseLinkingEnabled?: boolean;
    baseSnappingEnabled?: boolean;
  },
) {
  return (
    (!preview.baseDocument || preview.baseDocument === state.undoableState) &&
    (preview.baseLinkingEnabled === undefined ||
      preview.baseLinkingEnabled === state.isLinkingEnabled) &&
    (preview.baseSnappingEnabled === undefined ||
      preview.baseSnappingEnabled === state.isSnappingEnabled) &&
    !hasPendingTimelineEdit(state)
  );
}

export function captureExternalTarget(
  tracks: TrackType[],
  target: AssetDropTrackTarget,
): InsertionTarget {
  const index =
    target.type === "existing"
      ? target.trackIndex
      : target.type === "create-between"
        ? target.insertAtIndex
        : target.type === "create-at-top"
          ? 0
          : tracks.length;
  return captureInsertionTarget(tracks, index, target.type !== "existing");
}

/** No fabricated media, source caching, Undo or player mutation is used for drag presentation. */
export function previewIncomingPlacement(
  state: EditorState,
  target: InsertionTarget,
  kind: TrackKind,
  from: number,
  duration: number,
) {
  const index = incomingTrackIndex(state.undoableState.tracks, target, kind);
  const fail = () => ({ ok: false, state, inserted: false, index });
  if (
    index === null ||
    hasPendingTimelineEdit(state) ||
    !Number.isSafeInteger(from) ||
    from < 0 ||
    !Number.isSafeInteger(duration) ||
    duration < 1 ||
    !Number.isSafeInteger(from + duration)
  )
    return fail();
  if (target.type === "new") return { ok: true, state, inserted: false, index };
  const track = state.undoableState.tracks[index]!;
  if (track.locked || trackKind(track) !== kind) return fail();
  return {
    ...openPlacementSpace(
      state,
      [{ trackId: track.id, from, duration }],
      track.id,
    ),
    index,
  };
}
